/**
 * Video Upload Manager
 * Handles chunked video upload according to backend API documentation
 *
 * Features:
 * - Chunked upload with retry + exponential backoff
 * - Parallel chunk uploads (configurable concurrency)
 * - Session resumability via sessionStorage
 * - total_chunks correction after recording finishes
 */

import apiService from '@shared/services/api.service';

// sessionStorage key for persisting upload state across refreshes
const UPLOAD_STATE_KEY = 'ai_video_upload_state';

class VideoUploadManager {
  constructor(candidateId, authTokens) {
    this.candidateId = candidateId;
    this.authTokens = authTokens;
    this.uploadId = null;
    this.CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks (matches reference implementation)
    this.PARALLEL_LIMIT = 3; // Upload up to 3 chunks concurrently
  }

  /**
   * Upload complete video with progress tracking
   * @param {Blob} videoBlob - The video blob to upload
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Upload result
   */
  async uploadVideo(videoBlob, onProgress = () => {}) {
    try {
      // Step 1: Initialize upload session
      onProgress({ stage: 'initializing', progress: 0 });
      await this.initialize(videoBlob);

      // Step 2: Upload all chunks
      onProgress({ stage: 'uploading', progress: 0 });
      await this.uploadAllChunks(videoBlob, onProgress);

      // Step 3: Complete upload
      onProgress({ stage: 'finalizing', progress: 100 });
      const result = await this.complete();

      onProgress({ stage: 'done', progress: 100, result });
      return result;
    } catch (error) {
      console.error('Video upload failed:', error);

      // Try to abort if we have upload_id
      if (this.uploadId) {
        // Ensure error message is a string (not an object)
        const errorMsg = typeof error?.message === 'string'
          ? error.message
          : (typeof error === 'string' ? error : JSON.stringify(error));
        await this.abort('Upload failed: ' + errorMsg).catch(console.error);
      }

      throw error;
    }
  }

  /**
   * Initialize upload session
   * @param {Blob} videoBlob - The video blob
   */
  async initialize(videoBlob) {
    const totalChunks = Math.ceil(videoBlob.size / this.CHUNK_SIZE);

    // Clean MIME type - backend expects "video/webm" not "video/webm;codecs=vp9,opus"
    let mimeType = videoBlob.type || 'video/webm';
    // Strip codec info (everything after semicolon)
    if (mimeType.includes(';')) {
      mimeType = mimeType.split(';')[0];
    }

    // Build metadata matching backend expected format exactly
    const metadata = {
      total_chunks: totalChunks,
      total_size_bytes: videoBlob.size,
      mime_type: mimeType,
      original_filename: `interview_${this.candidateId}_${Date.now()}.webm`,
    };

    // Log exact payload for debugging
    console.log('Initializing video upload with metadata:', JSON.stringify(metadata, null, 2));

    try {
      const response = await apiService.initializeVideoUpload(
        this.candidateId,
        metadata
      );

      console.log('Upload init raw response:', JSON.stringify(response, null, 2));

      this.uploadId = response.upload_id;

      if (!this.uploadId) {
        console.error('No upload_id in response! Full response:', response);
        throw new Error('Backend did not return upload_id');
      }

      // Persist upload state for resumability
      this._saveState({ uploadId: this.uploadId, candidateId: this.candidateId, lastUploadedByte: 0 });

      console.log('Upload session initialized with upload_id:', this.uploadId);
    } catch (error) {
      console.error('Video upload init failed:', {
        status: error.status,
        message: error.message,
        data: error.data,
        fullError: JSON.stringify(error, null, 2),
      });
      throw error;
    }
  }

  /**
   * Upload all video chunks with parallel concurrency
   * @param {Blob} videoBlob - The video blob
   * @param {Function} onProgress - Progress callback
   */
  async uploadAllChunks(videoBlob, onProgress) {
    const totalChunks = Math.ceil(videoBlob.size / this.CHUNK_SIZE);
    let uploadedChunks = 0;

    // Upload in batches of PARALLEL_LIMIT
    for (let batchStart = 0; batchStart < totalChunks; batchStart += this.PARALLEL_LIMIT) {
      const batchEnd = Math.min(batchStart + this.PARALLEL_LIMIT, totalChunks);
      const batch = [];

      for (let i = batchStart; i < batchEnd; i++) {
        const start = i * this.CHUNK_SIZE;
        const end = Math.min(start + this.CHUNK_SIZE, videoBlob.size);
        const chunk = videoBlob.slice(start, end);
        batch.push(this.uploadChunkWithRetry(i, chunk, 3));
      }

      // Wait for the whole batch to complete
      await Promise.all(batch);

      uploadedChunks += (batchEnd - batchStart);
      const progress = Math.round((uploadedChunks / totalChunks) * 100);

      onProgress({
        stage: 'uploading',
        progress,
        current: uploadedChunks,
        total: totalChunks,
      });

      console.log(`Uploaded batch ${batchStart}-${batchEnd - 1} of ${totalChunks} (${progress}%)`);
    }
  }

  /**
   * Upload single chunk with retry logic
   * @param {number} index - Chunk index
   * @param {Blob} chunkData - Chunk data
   * @param {number} maxRetries - Maximum retry attempts
   */
  async uploadChunkWithRetry(index, chunkData, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await apiService.uploadVideoChunk(
          this.candidateId,
          this.uploadId,
          index,
          chunkData
        );
        return; // Success
      } catch (error) {
        lastError = error;
        console.warn(
          `Chunk ${index} upload attempt ${attempt} failed:`,
          error.message
        );

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * Math.pow(2, attempt - 1))
          );
        }
      }
    }

    throw new Error(
      `Failed to upload chunk ${index} after ${maxRetries} attempts: ${lastError.message}`
    );
  }

  /**
   * Update total_chunks on backend to reflect actual count after recording stops.
   * @param {number} actualTotalChunks - The real chunk count
   * @returns {boolean} true if update succeeded, false if caller should use allowPartial
   */
  async updateTotalChunks(actualTotalChunks) {
    if (!this.uploadId) return false;
    try {
      await apiService.updateVideoUploadChunks(
        this.candidateId,
        this.uploadId,
        actualTotalChunks,
      );
      console.log('Updated total_chunks on backend:', actualTotalChunks);
      return true;
    } catch (error) {
      // Non-fatal — caller should fall back to complete(allowPartial=true)
      console.warn('Failed to update total_chunks:', error.message);
      return false;
    }
  }

  /**
   * Complete upload session
   * @param {boolean} allowPartial - Allow completion with fewer chunks than initially specified
   * @returns {Promise<Object>} Upload result
   */
  async complete(allowPartial = false) {
    console.log('Completing video upload with:', {
      candidateId: this.candidateId,
      uploadId: this.uploadId,
      uploadIdType: typeof this.uploadId,
      allowPartial,
    });

    if (!this.uploadId) {
      throw new Error('Cannot complete upload: no upload_id available');
    }

    try {
      const result = await apiService.completeVideoUpload(
        this.candidateId,
        this.uploadId,
        allowPartial
      );
      console.log('Video upload completed:', result);
      this._clearState();
      return result;
    } catch (error) {
      console.error('Video upload complete failed:', {
        status: error.status,
        message: error.message,
        data: error.data,
        candidateId: this.candidateId,
        uploadId: this.uploadId,
      });
      throw error;
    }
  }

  /**
   * Abort upload session
   * @param {string} reason - Reason for aborting
   */
  async abort(reason) {
    if (!this.uploadId) return;

    console.log('Aborting video upload:', reason);
    await apiService.abortVideoUpload(this.candidateId, this.uploadId, reason);
    this._clearState();
  }

  /**
   * Check upload status
   * @returns {Promise<Object>} Upload status
   */
  async checkStatus() {
    if (!this.uploadId) {
      throw new Error('No upload session initialized');
    }

    return apiService.checkUploadStatus(this.candidateId, this.uploadId);
  }

  // =========================================================================
  // Session persistence (resumability across page refresh)
  // =========================================================================

  _saveState(state) {
    try {
      sessionStorage.setItem(UPLOAD_STATE_KEY, JSON.stringify(state));
    } catch { /* quota exceeded or private mode */ }
  }

  _clearState() {
    try {
      sessionStorage.removeItem(UPLOAD_STATE_KEY);
    } catch { /* ignore */ }
  }

  /**
   * Try to restore a previous upload session from sessionStorage.
   * Returns true if a session was restored, false otherwise.
   */
  static tryRestore(candidateId) {
    try {
      const raw = sessionStorage.getItem(UPLOAD_STATE_KEY);
      if (!raw) return null;
      const state = JSON.parse(raw);
      if (state.candidateId !== candidateId) return null;
      return state; // { uploadId, candidateId, lastUploadedByte }
    } catch {
      return null;
    }
  }

  /**
   * Resume a previously saved upload session.
   */
  restoreSession(uploadId, lastUploadedByte = 0) {
    this.uploadId = uploadId;
    console.log(`Restored upload session: ${uploadId}, resuming from byte ${lastUploadedByte}`);
  }
}

export default VideoUploadManager;
